// ./src/components/generic/RecordActions/RecordActions.tsx
import React from "react";
import { Group, Divider } from "@mantine/core";
import { AppButton } from "../AppButton";

export interface RecordActionsProps<T = any> {
  /** Context object (usually the record) passed to all action functions */
  context: T;

  /** Edit action handler */
  onDuplicateClick?: ((context: T) => void) | undefined;

  /** Edit action handler */
  onEditClick?: ((context: T) => void) | undefined;

  /** Remove/delete action handler */
  onRemoveClick?: ((context: T) => void) | undefined;

  /** View action handler */
  onViewClick?: ((context: T) => void) | undefined;

  /** Go into/navigate action handler (shows chevron right) */
  onGoInClick?: ((context: T) => void) | undefined;

  /** Loading state for edit action */
  editInProgress?: boolean;

  /** Loading state for remove action */
  removeInProgress?: boolean;

  /** Loading state for view action */
  viewInProgress?: boolean;

  /** Loading state for go in action */
  goInInProgress?: boolean;

  duplicateDisabled?: boolean;

  /** Disabled state for edit action */
  editDisabled?: boolean;

  /** Disabled state for remove action */
  removeDisabled?: boolean;

  /** Disabled state for view action */
  viewDisabled?: boolean;

  /** Disabled state for go in action */
  goInDisabled?: boolean;

  /** Tooltip for duplicate action */
  duplicateTooltip?: string;

  /** Tooltip for view action */
  viewTooltip?: string;

  /** Tooltip for edit action */
  editTooltip?: string;

  /** Tooltip for remove action */
  removeTooltip?: string;

  /** Tooltip for go in action */
  goInTooltip?: string;

  /** Size of the action buttons */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Gap between action buttons */
  gap?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Whether to show dividers between action groups */
  withDividers?: boolean;

  /** Additional className for styling */
  className?: string;

  /** Additional styles */
  style?: React.CSSProperties;
}

export const RecordActions = <T extends any>({
  context,
  onDuplicateClick,
  onEditClick,
  onRemoveClick,
  onViewClick,
  onGoInClick,
  editInProgress = false,
  removeInProgress = false,
  viewInProgress = false,
  goInInProgress = false,
  duplicateDisabled = false,
  editDisabled = false,
  removeDisabled = false,
  viewDisabled = false,
  goInDisabled = false,
  duplicateTooltip = "Duplicate the record",
  viewTooltip = "View the record",
  editTooltip = "Edit the record",
  removeTooltip = "Remove the record",
  goInTooltip = "Go to details",
  size = "xs",
  gap = "xs",
  withDividers = false,
  className,
  style,
}: RecordActionsProps<T>) => {
  const handleDuplicateClick = () => {
    if (onDuplicateClick && !duplicateDisabled) {
      onDuplicateClick(context);
    }
  };

  const handleEditClick = () => {
    if (onEditClick && !editDisabled && !editInProgress) {
      onEditClick(context);
    }
  };

  const handleRemoveClick = () => {
    if (onRemoveClick && !removeDisabled && !removeInProgress) {
      onRemoveClick(context);
    }
  };

  const handleViewClick = () => {
    if (onViewClick && !viewDisabled && !viewInProgress) {
      onViewClick(context);
    }
  };

  const handleGoInClick = () => {
    if (onGoInClick && !goInDisabled && !goInInProgress) {
      onGoInClick(context);
    }
  };

  // Group primary actions (view, edit)
  const primaryActions: any[] = [];

  if (onViewClick) {
    primaryActions.push(
      <AppButton
        key="view"
        variant="tertiary"
        iconName="Eye"
        size={size}
        onClick={handleViewClick}
        inProgress={viewInProgress}
        disabled={viewDisabled}
        tooltip={viewTooltip}
      />
    );
  }

  if (onDuplicateClick) {
    primaryActions.push(
      <AppButton
        key="duplicate"
        variant="tertiary"
        iconName="Copy"
        size={size}
        onClick={handleDuplicateClick}
        disabled={duplicateDisabled}
        tooltip={duplicateTooltip}
      />
    );
  }

  if (onEditClick) {
    primaryActions.push(
      <AppButton
        key="edit"
        variant="tertiary"
        iconName="Edit"
        size={size}
        onClick={handleEditClick}
        inProgress={editInProgress}
        disabled={editDisabled}
        tooltip={editTooltip}
      />
    );
  }

  // Destructive actions (remove)
  const destructiveActions: any[] = [];

  if (onRemoveClick) {
    destructiveActions.push(
      <AppButton
        key="remove"
        variant="danger"
        iconName="Trash"
        size={size}
        onClick={handleRemoveClick}
        inProgress={removeInProgress}
        disabled={removeDisabled}
        tooltip={removeTooltip}
      />
    );
  }

  // Navigation actions (go in)
  const navigationActions: any[] = [];

  if (onGoInClick) {
    navigationActions.push(
      <AppButton
        key="goIn"
        variant="tertiary"
        iconName="ChevronRight"
        size={size}
        onClick={handleGoInClick}
        inProgress={goInInProgress}
        disabled={goInDisabled}
        tooltip={goInTooltip}
      />
    );
  }

  // If no actions are provided, return null
  if (
    primaryActions.length === 0 &&
    destructiveActions.length === 0 &&
    navigationActions.length === 0
  ) {
    return null;
  }

  const renderActionsWithDividers = () => {
    const groups = [];

    if (primaryActions.length > 0) {
      groups.push(
        <Group key="primary" gap={gap}>
          {primaryActions}
        </Group>
      );
    }

    if (destructiveActions.length > 0) {
      groups.push(
        <Group key="destructive" gap={gap}>
          {destructiveActions}
        </Group>
      );
    }

    if (navigationActions.length > 0) {
      groups.push(
        <Group key="navigation" gap={gap}>
          {navigationActions}
        </Group>
      );
    }

    return groups.map((group, index) => (
      <React.Fragment key={index}>
        {group}
        {withDividers && index < groups.length - 1 && (
          <Divider orientation="vertical" />
        )}
      </React.Fragment>
    ));
  };

  const renderActionsSimple = () => {
    return [...primaryActions, ...destructiveActions, ...navigationActions];
  };

  return (
    <Group
      gap={gap}
      justify="flex-end"
      className={className || ""}
      style={style}
    >
      {withDividers ? renderActionsWithDividers() : renderActionsSimple()}
    </Group>
  );
};
